import * as vp from 'vopay';
import { Box, Table, usePagination, Pagination } from 'vopay';
import { FunctionComponent, useState, useMemo } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import NestedTransactionsTable from '../NestedTransactionsTable/NestedTransactionsTable';
import ContactNameLink from '../ContactNameLink';
import type { DateFilterType } from '../AccountingSystemSearchForm';

import { Button } from '#shared/components/UI';

type AccountingSystemContainerProps = {
  BillsOrInvoices: vp.BillOrInvoiceRailz[];
  ServiceName: string;
  Type: 'Payable' | 'Receivable';
  // Which date the tab is anchored on: the selected date leads the columns and
  // the other date sits after Amount.
  DateFilterType: DateFilterType;
  // Hidden when the list is scoped to one contact's own view.
  showContactColumn?: boolean;
  // Opens the contact detail drawer for a row's linked record.
  onContactClick?: (clientAccountId: string) => void;
};
type FC = FunctionComponent<AccountingSystemContainerProps>;

const AccountingSystemClosed: FC = ({
  BillsOrInvoices,
  Type,
  ServiceName,
  DateFilterType,
  showContactColumn = true,
  onContactClick,
}: AccountingSystemContainerProps) => {
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);

  const { t } = useTranslation('translations', { keyPrefix: 'Shared.AccountingSystem.CloseTab' });

  // pagination
  const totalDataLength = useMemo(() => {
    return BillsOrInvoices.length || 0;
  }, [BillsOrInvoices]);

  const PER_PAGE = 20;

  const { pageCount, pageIndex, setPageIndex, offset } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const settlementDateHeader =
    Type === 'Receivable'
      ? t('ColumnHeader.receivedDate', { defaultValue: 'Received Date' })
      : t('ColumnHeader.paymentDate', { defaultValue: 'Payment Date' });

  const documentDateHeader =
    Type === 'Receivable'
      ? t('ColumnHeader.invoiceDate', { defaultValue: 'Invoice Date' })
      : t('ColumnHeader.billDate', { defaultValue: 'Bill Date' });

  const renderSettlementDate = ({ data }: { data: vp.BillOrInvoiceRailz }) =>
    getLastPaymentDate(data.PaymentDetails, data.postedDate);

  const renderDocumentDate = ({ data }: { data: vp.BillOrInvoiceRailz }) =>
    data.postedDate ? format(new Date(data.postedDate.replace('Z', '')), 'yyyy-MM-dd') : '';

  // The date the tab is anchored on leads the columns; the other date sits
  // after Amount so both stay visible whichever anchoring is selected. Each
  // date column is declared in both positions and toggled via `visible`.
  const isDocumentDateAnchored = DateFilterType === 'documentDate';

  return (
    <Box flx="col" width="100%">
      <Box>
        <Table
          id="closed-bills-invoices"
          version="2"
          columns={[
            {
              id: 'documentDateLeading',
              header: documentDateHeader,
              align: 'left',
              visible: isDocumentDateAnchored,
              template: renderDocumentDate,
            },
            {
              id: 'lastPaymentDate',
              header: settlementDateHeader,
              align: 'left',
              visible: !isDocumentDateAnchored,
              template: renderSettlementDate,
            },
            {
              id: 'name',
              header:
                Type === 'Receivable'
                  ? t('ColumnHeader.customer', { defaultValue: 'Customer' })
                  : t('ColumnHeader.supplier', { defaultValue: 'Supplier' }),
              align: 'left',
              visible: showContactColumn,
              template: ({ data }) => <ContactNameLink row={data} onOpenContact={onContactClick} />,
            },
            {
              id: 'invoiceNumber',
              header:
                Type === 'Receivable'
                  ? t('ColumnHeader.invoiceNumber', { defaultValue: 'Invoice No.' })
                  : t('ColumnHeader.billNumber', { defaultValue: 'Bill No.' }),
              align: 'left',
              template: ({ data }) => data.invoiceNumber || data.vendorInvoiceNumber || '',
            },
            {
              id: 'amount',
              header: t('ColumnHeader.amount', { defaultValue: 'Amount' }),
              align: 'left',
              template: ({ data }) => `${data?.currency || ''} ${vp.formattedCurrency(data.totalAmount.toString())}`,
            },
            {
              id: 'lastPaymentDateTrailing',
              header: settlementDateHeader,
              align: 'left',
              visible: isDocumentDateAnchored,
              template: renderSettlementDate,
            },
            {
              id: 'documentDate',
              header: documentDateHeader,
              align: 'left',
              visible: !isDocumentDateAnchored,
              template: renderDocumentDate,
            },
            {
              id: 'actions',
              header: t('ColumnHeader.actions', { defaultValue: 'Actions' }),
              align: 'right',
              template: ({ data }) => (
                <Button
                  id={`toggle-detail-${data.id || ''}`}
                  colorScheme="secondary"
                  size="small"
                  iconRight={expandedRowIds.includes(data.id) ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                  onClick={() => {
                    setExpandedRowIds((ids) =>
                      ids.includes(data.id) ? ids.filter((rowId) => rowId !== data.id) : [...ids, data.id],
                    );
                  }}
                >
                  {expandedRowIds.includes(data.id)
                    ? t('Row.hideDetailButton', { defaultValue: 'Hide Detail' })
                    : t('Row.showDetailButton', { defaultValue: 'Show Detail' })}
                </Button>
              ),
            },
          ]}
          rows={BillsOrInvoices.map((item, i) => ({
            id: item.id || String(i),
            data: item,
          })).slice(offset, offset + PER_PAGE)}
          expandedRowIds={expandedRowIds}
          renderExpandedContent={({ data }) => (
            <NestedTransactionsTable
              DocumentID={data.id}
              Payments={data.PaymentDetails}
              Currency={data.currency || ''}
              ServiceName={ServiceName}
              PaidAmount={data.totalAmount}
            />
          )}
        />
      </Box>

      <Box width="100%" jc="center" mt="lg" mb="sm">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={({ selected }) => setPageIndex(selected)}
          />
        )}
      </Box>
    </Box>
  );
};

export function getPaymentMethodFromPayments(payments: vp.BillOrInvoiceRailzPaymentDetails[]): string {
  const paymentMethods = payments.map((payment) => (payment.PaymentMethod ? payment.PaymentMethod : null));
  const uniquePaymentMethods = [...new Set(paymentMethods)];

  if (uniquePaymentMethods.length === 0) {
    return '';
  }

  // Case 1: All payments have the same PaymentMethod and CreditAmount is 0 or null
  const allCreditAmountsZeroOrNull = payments.every(
    (payment) => Number(payment.CreditAmount) === 0 || payment.CreditAmount === null,
  );

  if (uniquePaymentMethods.length === 1 && allCreditAmountsZeroOrNull) {
    return uniquePaymentMethods[0] ?? '';
  }

  // Case 2: All payments have 0 or null for PaymentMethod but CreditAmount is not null
  const allCredits = payments.every(
    (payment) =>
      payment.CreditAmount !== null &&
      Number(payment.CreditAmount) > 0 &&
      (Number(payment.TransactionAmount) === 0 || !payment.TransactionAmount),
  );

  if (allCredits) {
    return 'Paid using Credits';
  }

  // Case 3: No payment methods supplied or all are blank, and CreditAmount is blank
  if (uniquePaymentMethods.length === 0 || uniquePaymentMethods.every((method) => method === null)) {
    const allCreditAmountsNull = payments.every((payment) => !payment.CreditAmount);

    if (allCreditAmountsNull) {
      return '';
    }
  }

  // Case 4: Otherwise, multiple payment methods
  return 'Multiple';
}

export function getLastPaymentDate(payments: vp.BillOrInvoiceRailzPaymentDetails[], invoiceDate: string): string {
  const paymentDates = payments.map((payment) => (payment.TransactionDate ? payment.TransactionDate : ''));
  const lastPaymentDate = paymentDates.reduce((a, b) => (a > b ? a : b), '');
  const finalDate = lastPaymentDate || invoiceDate;

  // PostedDate is nullable on the wire (the adapter maps it to ''), and a
  // payment row can lack a TransactionDate — format() throws on an invalid
  // date, which would take the whole closed table (or export) down.
  const parsed = new Date(finalDate);

  return finalDate && !Number.isNaN(parsed.getTime()) ? format(parsed, 'yyyy-MM-dd') : '';
}

export default AccountingSystemClosed;
